import { DetectionZone } from "@app/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { getShapeCenter } from "../../../../helpers/mapHelpers";
import { ICoordsHelper } from "../../../../helpers/coordsHelpers";
import { MapShapeTypes } from "../../maps";
import { OmniMapMethods } from "../../maps/OmniGoogleMap";
import { ShapeManagerState } from "./useShapeManager";
import { ToolManagerState } from "./useToolManager";

interface UseMapManagerProps {
    mapRef: React.RefObject<OmniMapMethods>;
    coordsHelper: ICoordsHelper | undefined;
    shapeManager: ShapeManagerState;
    toolManager: ToolManagerState;
}

export interface MapManagerState {
    selectionBox: DetectionZone | undefined;

    handleMapClick: () => void;
    handleShapeClick: (e: google.maps.MapMouseEvent, shape: MapShapeTypes) => void;
    startSelectionBox: () => void;
    updateSelectionBox: (box?: DetectionZone) => void;
    panToShape: (shape: MapShapeTypes) => void;
    centerMap: () => void;

    cleanup: () => void;
}

export function useMapManager({ mapRef, coordsHelper, shapeManager, toolManager }: UseMapManagerProps): MapManagerState {
    const [selectionBox, setSelectionBox] = useState<DetectionZone | undefined>();

    const activeListeners = useRef<google.maps.MapsEventListener[]>([]);

    const cleanup = useCallback(() => {
        activeListeners.current.forEach((listener) => {
            if (listener && listener.remove) {
                listener.remove();
            }
        });
        activeListeners.current = [];
    }, []);

    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    const handleMapClick = useCallback(() => {
        if (shapeManager.selectedShapes.length) {
            const hasIncompleteShape = shapeManager.selectedShapes.length === 1 && !shapeManager.selectedShapes[0].isComplete();
            if (!hasIncompleteShape) {
                shapeManager.clearSelection();
            }
        }
    }, [shapeManager]);

    const handleShapeClick = useCallback(
        (e: google.maps.MapMouseEvent, shape: MapShapeTypes) => {
            if (e.domEvent && (e.domEvent as any).button !== undefined) {
                if ((e.domEvent as MouseEvent).button === 2 && toolManager.editMode === 1) {
                    e.stop?.();
                    shapeManager.selectShapes([shape]);
                } else if ((e.domEvent as MouseEvent).button === 0) {
                    shapeManager.selectShapes([shape]);
                }
            }
        },
        [shapeManager, toolManager.editMode]
    );

    const startSelectionBox = useCallback(() => {
        const selectedShape = shapeManager.selectedShapes[0];
        if (selectedShape && !selectedShape.isComplete()) {
            const shapeToRemove = shapeManager.localShapes.find((s) => s.id === selectedShape.id);
            if (shapeToRemove) {
                shapeManager.deleteShape(shapeToRemove.id!);
            }
        }

        toolManager.clearTool();

        if (!selectionBox) {
            const newSelectionBox = new DetectionZone();
            newSelectionBox.id = "layoutMapSelectTool";
            newSelectionBox.name = "layoutMapSelectTool";
            setSelectionBox(newSelectionBox);
        } else {
            setSelectionBox(undefined);
        }
    }, [selectionBox, shapeManager, toolManager]);

    const updateSelectionBox = useCallback(
        (box?: DetectionZone) => {
            if (box?.isComplete() && coordsHelper) {
                const outerPoly = new google.maps.Polygon({
                    paths: [box.points?.map(coordsHelper.toLatLng)],
                });

                const selectedShapes: MapShapeTypes[] = [];
                shapeManager.allShapes
                    .filter((shape) => shape.$type !== "Spot" && shape.$type !== "Device")
                    .forEach((shape) => {
                        const shapePoints = (shape as any).points?.map(coordsHelper.toLatLng);
                        if (shapePoints?.every((point: google.maps.LatLng) => google.maps.geometry.poly.containsLocation(point, outerPoly))) {
                            selectedShapes.push(shape as MapShapeTypes);
                        }
                    });

                shapeManager.selectShapes(selectedShapes);
                setSelectionBox(undefined);

                outerPoly.setMap(null);
            } else if (box) {
                setSelectionBox(box);
            }
        },
        [coordsHelper, shapeManager]
    );

    const panToShape = useCallback(
        (shape: MapShapeTypes) => {
            if (!coordsHelper || !mapRef.current?.map) return;

            const map = mapRef.current.map;

            if (shape.isComplete() && coordsHelper) {
                const center = getShapeCenter(shape, coordsHelper);
                map.panTo(center);
                setTimeout(() => {
                    shapeManager.selectShapes([shape]);
                }, 100);
            } else {
                mapRef.current.reCenter?.();
                shapeManager.selectShapes([shape]);
            }
        },
        [coordsHelper, mapRef, shapeManager]
    );

    const centerMap = useCallback(() => {
        mapRef.current?.reCenter?.();
    }, [mapRef]);

    return {
        selectionBox,
        handleMapClick,
        handleShapeClick,
        startSelectionBox,
        updateSelectionBox,
        panToShape,
        centerMap,
        cleanup,
    };
}
